import { FC, TransitionEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, CheckCheck, Settings, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/components/ui/sidebar';
import { NAV_RAIL_WIDTH, useNavModeStore } from '@/stores/nav-mode-store';
import { useNotificationCenterStore } from '@/stores/notification-center-store';
import { useGetNotifications } from '@/hooks/queries/use-get-notifications';
import { useMarkAllNotificationsRead } from '@/hooks/mutations/use-mark-all-notifications-read';
import { ActivityFeed } from './activity-feed';

export const NotificationCenter: FC = () => {
  const { isMobile } = useSidebar();
  const navMode = useNavModeStore((state) => state.mode);
  const isOpen = useNotificationCenterStore((s) => s.isOpen);
  const close = useNotificationCenterStore((s) => s.close);
  const openPreferences = useNotificationCenterStore((s) => s.openPreferences);
  const { data: feedData } = useGetNotifications();
  const markAllRead = useMarkAllNotificationsRead();

  const hasUnread = (feedData?.unread_count ?? 0) > 0;

  const [isRendered, setIsRendered] = useState(isOpen);
  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setIsShown(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    setIsShown(false);
  }, [isOpen]);

  if (!isRendered) return null;

  const handleTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isOpen) setIsRendered(false);
  };

  // Anchored to the rail, whose width depends on the taskbar mode. Inline
  // rather than a class: Tailwind only emits class names it can see literally.
  const leftOffset = { left: isMobile ? 0 : NAV_RAIL_WIDTH[navMode] };

  return createPortal(
    <>
      <div
        className={cn(
          'fixed top-0 right-0 bottom-0 z-50 bg-black/50',
          'transition-opacity duration-300 ease-in-out',
          isShown ? 'opacity-100' : 'opacity-0',
        )}
        style={leftOffset}
        onClick={close}
        aria-hidden
      />
      <div
        className='fixed top-0 bottom-0 z-50 w-[483px] max-w-[90vw] overflow-hidden'
        style={leftOffset}
      >
        <div
          className={cn(
            'flex h-full w-full flex-col border-r bg-background',
            'transition-transform duration-300 ease-in-out',
            isShown ? 'translate-x-0' : '-translate-x-full',
          )}
          onTransitionEnd={handleTransitionEnd}
          role='dialog'
          aria-label='Activity'
        >
          <div className='flex items-center justify-between p-2'>
            <div className='flex items-center gap-2 px-2 py-1.5'>
              <Bell className='size-4 text-foreground' />
              <span className='text-sm font-medium text-foreground'>
                Activity
              </span>
            </div>
            <div className='flex items-center gap-2'>
              {hasUnread && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => markAllRead.mutate()}
                  disabled={markAllRead.isPending}
                  className='gap-1.5'
                >
                  <CheckCheck className='size-4' />
                  Mark all as read
                </Button>
              )}
              <Button
                variant='outline'
                size='icon'
                onClick={openPreferences}
                aria-label='Notification settings'
              >
                <Settings className='size-4' />
              </Button>
              <Button
                variant='outline'
                size='icon'
                onClick={close}
                aria-label='Close activity panel'
              >
                <X className='size-4' />
              </Button>
            </div>
          </div>
          <ActivityFeed />
        </div>
      </div>
    </>,
    document.body,
  );
};
